import { createFileRoute, Navigate, useLocation } from "@tanstack/react-router";
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import EmailVerifyPage from "@/site/pages/email-verify";

/**
 * Legacy URL parity — `/verify` (PRD 13, 2026-09-15, Nick). Legacy served this at
 * `bday/web.py:1320` and it appears in every verification email ever sent, so the path
 * is kept rather than renamed to `/email-verify`.
 *
 * Old-email landing (2026-10-20, cutover night): legacy verification links carry
 * `?t=<tracking id>&e=<verify token>`, and legacy marked the address verified from them.
 * v2 never does — `e` is md5(email)[:16], derivable from the address alone, so it proves
 * nothing (PRD 13 D18). A signed-out visitor holding such a link used to be bounced to the
 * home page with no explanation; now they are told the link is from the old site and how
 * to verify with a fresh code. Without `t`/`e` the behaviour is unchanged.
 */
function LegacyVerifyLink({ signedIn }: { signedIn: boolean }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
              <MailCheck className="w-7 h-7 text-muted-foreground" />
            </div>
            <h1 className="text-xl font-semibold" data-testid="text-verify-legacy">
              {signedIn ? "Your email is already verified" : "This link is from an older email"}
            </h1>
            <p className="text-muted-foreground text-base">
              {signedIn
                ? "There's nothing left to do here."
                : "BirthdayAlarm has a new website. Sign in and, if your email still needs verifying, we'll send you a fresh code."}
            </p>
            {!signedIn && (
              <Button
                onClick={() => (window.location.href = "/login?next=/verify")}
                className="mt-4"
                data-testid="button-verify-legacy-login"
              >
                Sign in
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/")}
              className={signedIn ? "mt-4" : undefined}
              data-testid="button-verify-legacy-home"
            >
              Go to BirthdayAlarm
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function VerifyRoute() {
  const { user, isLoading } = useAuth();
  const search = useLocation({ select: (l) => l.search as Record<string, unknown> });
  const isLegacyLink = search.t != null && search.e != null;
  // The root layout already intercepts an UNVERIFIED member, so reaching this route
  // normally means there's nothing left to verify — an old email link or a bookmark.
  if (isLoading) return null;
  if (user && !user.is_email_verified) return <EmailVerifyPage />;
  if (isLegacyLink) return <LegacyVerifyLink signedIn={!!user} />;
  return <Navigate to="/" replace />;
}

export const Route = createFileRoute("/verify")({
  component: VerifyRoute,
});
